import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductPriceStyles } from "@tCentro/Components/CENCustomProductPrice";
import CENProductPriceCustomPDP, { ProductPriceStylesPDP } from "@tCentro/Components/CENProductPriceCustomPDP";
import NISProductPriceCustomPDP from "@tCentro/Components/NISProductPriceCustomPDP";
import cenQtyMin from "@tCentro/Handlers/CenQtyMinHandler";
import cenQtyMulti from "@tCentro/Handlers/CenQtyMultiHandler";
import CENErrorIcon from "@tCentro/Icons/CENErrorIcon";
import ProductAddToCartButton from "@tCentro/Overrides/Components/ProductAddToCartButton";
import { InfoIconTooltipStyles } from "@tCentro/Overrides/Components/ProductAddToCartButton";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@tCentro/Overrides/Components/ProductPrimaryImage";
import ProductQuantityOrdered from "@tCentro/Overrides/Components/ProductQuantityOrdered";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY, NISCO } from "@tCentro/theme/design";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState & CustomApplicationState, ownProps: HasProduct) => {
    const { productInfo, product } = ownProps;
    return {
        selectedImage: getSelectedImage(state, product),
        configurationSelection: state.pages.productDetails.configurationSelection,
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        qtyOrdered: productInfo.qtyOrdered,
        isVariantParent: product.isVariantParent,
        productSettings: getSettingsCollection(state).productSettings,
        productContext: ownProps,
        subscription: state.pages.productDetails.subscription,
        mutipleSaleQty: product?.detail?.multipleSaleQty,
        roundingRule: product?.detail?.roundingRule,
        isMinErrorObj: state.t_components.isMinError,
        isMultiErrorObj: state.t_components.isMultiError,
    };
};

const mapDispatchToProps = {
    cenQtyMin,
    cenQtyMulti,
};

export interface ScrollingBarStyles {
    tooltipContainerStyles?: InjectableCss;
    errorMsgStyle?: InjectableCss;
    errorIconStyles?: IconPresentationProps;
    errorMsgStyleTypo?: TypographyPresentationProps;
    mainContainer?: GridContainerProps;
    productImageWrapper?: GridItemProps;
    productPriceWrapper?: GridItemProps;
    productQTyWrapper?: GridItemProps;
    productAddtoCartWrapper?: GridItemProps;
    productTitleWrapper?: GridItemProps;
    primaryImage?: ProductPrimaryImageStyles;
    brandGridItem?: InjectableCss;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: InjectableCss;
    titleText?: TypographyProps;
    labelText?: TypographyProps;
    labelQtyText?: TypographyProps;
    labelValue?: TypographyProps;
    valueText?: TypographyProps;
    productPrice?: ProductPriceStylesPDP;
    quantityOrderedWrapper?: InjectableCss;
    addQTYBtn?: ButtonProps;
    subQTYBtn?: ButtonProps;
    quantityOrdered?: TextFieldProps;
    button?: ButtonPresentationProps & InfoIconTooltipStyles;
    leftPanelRow?: GridItemProps;
    rightPanelRow?: GridItemProps;
    hideMainContainer?: GridContainerProps;
}

export const scrollingBarStyles: ScrollingBarStyles = {
    errorMsgStyleTypo: {
        color: "danger",
        css: css`
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
        `,
    },
    errorMsgStyle: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 4px;
            position: absolute;
            margin-top: 4px;
            bottom: 30px;
            margin-left: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        bottom: 2px;
                    `,
                    null,
                    null,
                ])}
            @media (min-width: 1024px) and (max-width: 1279px) {
                bottom: 8px;
            }
        `,
    },
    tooltipContainerStyles: {
        css: css`
            background-color: #262525;
            border-radius: 4px;
            width: max-content;
            border-radius: 4px;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            position: absolute;
            top: -29px;
            right: 30px;
            z-index: 999;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
            padding: 8px 16px;
            color: #262525;
            @media (max-width: 1024px) {
                top: -26px;
                right: 40px;
            }
            &:after {
                content: "";
                border-left: 0px solid ${getProp("theme.typography.p.color")};
                border-right: 4px solid ${getProp("theme.typography.p.color")};
                position: absolute;
                z-index: 999;
                top: 29px;
                left: 42%;
                width: 0px;
                height: 16px;
                width: 0px;
                height: 0px;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-top: 16px solid ${getProp("theme.typography.p.color")};
            }
            @media (min-width: 1024px) and (max-width: 1279px) {
                right: 50px;
            }
        `,
    },
    mainContainer: {
        css: css`
            display: flex;
            gap: 24px;
            margin: 0 auto;
            width: 100%;
            position: fixed;
            top: 0px;
            left: 0px;
            right: 0px;
            z-index: 9999;
            background: #fff;
            padding: 24px 64px;
            box-shadow: 0px 4px 8px 0px #2625253d;
            transition: all 0.5s ease-in-out;
            max-width: 1800px;

            opacity: 1;
            transform: translateY(0);

            &.hideBar {
                opacity: 0;
                transform: translateY(-100%);
                z-index: -1;
            }

            > div[class^="GridOffset"] {
                display: flex;
                flex-wrap: wrap;
                width: 100%;
                justify-content: center;
                align-items: center;
            }
            @media (min-width: 992px) and (max-width: 1199px) {
                padding: 20px 20px 15px 35px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        padding: 16px;
                        gap: 16px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    hideMainContainer: {
        css: css`
            transition: all 1s;
            z-index: -1;
            opacity: 0;
        `,
    },
    productImageWrapper: {
        css: css`
            max-width: 24%;
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null, null])}
        `,
    },
    productTitleWrapper: {
        css: css`
            max-width: 78%;
            flex-direction: column;
            margin-top: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin-top: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    productPriceWrapper: {
        css: css`
            align-items: center;
            padding-top: 0px;
            position: relative;
            @media (min-width: 992px) and (max-width: 1199px) {
                div {
                    justify-content: end;
                }
                span {
                    text-align: right;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                        align-items: flex-end;
                        justify-content: end;
                        padding-left: 0px;
                        padding-right: 0px;
                        div {
                            justify-content: end;
                        }
                        span {
                            text-align: right;
                        }
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                        align-items: flex-end;
                        justify-content: end;
                    `,
                    null,
                ])}
        `,
    },
    productQTyWrapper: {
        css: css`
            align-items: center;
            padding-top: 0px;
            margin-bottom: 11px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        display: flex;
                        justify-content: flex-end;
                    `,
                    css`
                        display: flex;
                        justify-content: flex-end;
                    `,
                    css`
                        display: flex;
                        justify-content: flex-end;
                    `,
                ])}
        `,
    },
    productAddtoCartWrapper: {
        css: css`
            justify-content: center;
            align-items: center;
            padding-top: 0px;
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, css``, null, null])}
        `,
    },
    primaryImage: {
        centeringWrapper: {
            css: css`
                min-height: 130px !important;
                padding: 0px !important;
                img {
                    max-height: 185px;
                    width: 100% !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            img {
                                width: 100% !important;
                                max-height: 109px !important;
                            }
                        `,
                        null,
                        null,
                    ])}
            `,
        },
        productImageWrapper: {
            css: css`
                width: 185px !important;
                height: 185px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            width: 109px !important;
                            height: 109px !important;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
        productDisclaimerMsg: {
            css: css`
                display: none;
            `,
        },
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 18,
            weight: 600,
            css: css`
                line-height: 24px !important;
                span {
                    color: #000000 !important;
                }
                color: #000000 !important;
                font-family: Roboto Condensed;
            `,
        },
        nameLink: {
            css: css`
                > span {
                    font-size: 18px;
                    line-height: 24px;
                    font-family: Roboto Condensed;
                    color: #000000 !important;
                }
            `,
        },
    },
    titleText: {
        css: css`
            font-family: Roboto Condensed;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: 24px;
            line-height: 32px;
            color: #2b5197;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: normal;
            margin-bottom: 3px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        -webkit-line-clamp: 3;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    labelText: {
        css: css`
            font-size: 18px;
            font-weight: 600;
            line-height: 24px;
            color: #444444;
            font-family: Roboto Condensed;
        `,
    },
    labelQtyText: {
        css: css`
            margin-right: 4px;
            font-size: 18px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 18px;
            color: ${getProp("theme.typography.p.color")};
            font-family: Roboto;
        `,
    },
    labelValue: {
        css: css`
            font-size: 18px;
            font-weight: 600;
            line-height: 24px;
            color: #444444;
            font-family: Roboto Condensed;
        `,
    },
    valueText: {
        size: 14,
        css: css`
            margin-bottom: 3px;
        `,
    },
    productPrice: {
        priceWrapper: {
            css: css`
                align-self: flex-end;
            `,
        },
        parentLabel: {
            css: css`
                border: 2px solid black;
            `,
        },
        unitListPriceText: {
            css: css`
                border: 2px solid blue !important;
            `,
        },
        regularPrice: {
            css: css`
                align-self: flex-end;
                .savingsPer {
                    font-size: 18px !important;
                    line-height: 24px !important;
                }
                .unitPrice {
                    font-size: 18px;
                    line-height: 24px;
                }
            `,
        },
        price: {
            priceText: { size: 26 },
            unitOfMeasureText: { size: 26 },
            unitListPriceText: {
                css: css`
                    font-size: 24px !important;
                    line-height: 32px !important;
                `,
            },
        },
        requireDrawingApprovalWrapper: {
            css: css`
                padding: 0px;
            `,
        },
    },
    quantityOrderedWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            > [class*="FormFieldStyle-"] {
                label {
                    display: none;
                }
            }
        `,
    },
    addQTYBtn: {
        css: css`
            height: 16px;
            width: 16px;
            min-height: 0px;
            margin-top: 0px;
            background-color: ${COLORS.white};
            padding: 0px;
            position: absolute;
            right: 16px;
            z-index: 1;
            border: none;
            & span {
                width: 16px;
                height: 16px;
                top: 0px !important;
            }
            & svg {
                width: 16px;
                height: 16px;
                color: ${COLORS.black};
            }
            &:hover {
                background-color: ${COLORS.white};
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background: transparent;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        right: 15px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    subQTYBtn: {
        css: css`
            width: 16px;
            height: 16px;
            margin-top: 0px;
            min-height: 0px;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 64px;
            z-index: 1;
            border: none;
            & span {
                width: 16px;
                height: 16px;
                top: 0px !important;
            }
            & svg {
                color: ${COLORS.black};
                width: 16px;
                height: 16px;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background: transparent;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        left: 60px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-top: 0px;
                width: initial;
                flex-direction: row !important;
                align-items: center;

                label {
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    font-size: ${TYPOGRAPHY.size.base};
                    line-height: 24px;
                    width: 35% !important;
                }

                > div {
                    margin-top: 0 !important;

                    input {
                        padding: 11px 15px 11px 15px !important;
                        height: 48px !important;
                        width: 130px !important;
                        border: 1px solid #262525;
                        text-align: center;
                        border-radius: 16px !important;
                        margin-left: 4px;
                        font-family: Roboto;
                    }
                }

                @media (min-width: 992px) and (max-width: 1180px) {
                    > div {
                        input {
                            width: 150px !important;
                        }
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            > div {
                                input {
                                    width: 130px !important;
                                }
                            }
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    button: {
        fastGuestCheckoutTextSyltes: {
            css: css`
                text-transform: capitalize !important;
                font-weight: 500 !important;
                font-size: 14px !important;
                line-height: 14px !important;
                font-style: italic;
            `,
        },
        css: css`
            width: 100%;
            border-radius: 4px !important;
            width: 176px !important;
            height: 48px !important;
            white-space: nowrap;
            display: flex;
            flex-direction: column;
            justify-content: center;
            &:disabled {
                background: ${getColor("secondary")};
                border-color: ${getColor("secondary")};
                opacity: 0.5;
            }
            > span:first-of-type {
                font-size: ${TYPOGRAPHY.size.large};
                font-family: ${TYPOGRAPHY.fontFamily.base};
                line-height: 18px;
                text-align: center;
                font-weight: 700 !important;
                font-family: Roboto Condensed;
                ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null, null])}
            }
            span {
                font-family: Roboto Condensed !important;
                text-transform: uppercase;
                align-self: center;
            }
            > span:second-of-type {
                text-transform: capitalize !important;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: max-content !important;
                        `,
                        css`
                            margin-top: 16px;
                        `,
                        css`
                            width: 100% !important;
                        `,
                        css`
                            width: 100% !important;
                        `,
                        css`
                            width: 100% !important;
                        `,
                    ],
                    "max",
                )}
            @media (min-width:992px) and (max-width:1180px) {
                width: 100% !important;
            }
        `,

        hiddenMobileToolTip: {
            css: css`
                display: inline;
                position: relative;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            left: 35px;
                            top: 12px;
                            position: absolute;
                        `,
                        css`
                            left: 35px;
                            top: 28px;
                            position: absolute;
                        `,
                        css`
                            left: 4%;
                            top: 12px;
                            position: absolute;
                        `,
                        css`
                            @media (min-width: 992px) and (max-width: 1024px) {
                                top: 12px;
                                left: 10%;
                                position: absolute;
                            }
                        `,
                        null,
                    ])}
            `,
        },
        mobileTooltip: {
            cssOverrides: {
                tooltipBody: css`
                    left: 0px;
                `,
            },
        },
        tooltipButtonContainer: {
            css: css`
                display: flex;
                position: relative;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                            margin-top: 0px;
                        `,
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                            margin-top: 0px;
                        `,
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    leftPanelRow: {
        css: css`
            gap: 24px;
            padding: 0px;
            display: flex;
            max-width: 83.7%;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        gap: 16px !important;
                        max-width: 66.2%;
                        width: 100%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    rightPanelRow: {
        // width: [6, 6, 5, 6, 5],
        css: css`
            gap: 30px;
            padding: 0px;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        max-width: 32%;
                        width: 100%;
                    `,
                    css`
                        max-width: 14.7%;
                        width: 100%;
                    `,
                    css`
                        max-width: 14.7%;
                        width: 100%;
                    `,
                ])}
            @media(min-width:992px) and (max-width:1180px) {
                max-width: 20.7% !important;
            }
            @media (min-width: 1368px) {
                display: flex;
                justify-content: flex-end;
                gap: 0px;
                flex-direction: column;
                align-items: flex-end;
            }
        `,
    },
};

const styles = scrollingBarStyles;

const NISPdpScrollingHeader = ({
    productContext,
    selectedImage,
    productSettings,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    isVariantParent,
    qtyOrdered,
    subscription,
    mutipleSaleQty,
    roundingRule,
    isMinErrorObj,
    isMultiErrorObj,
    cenQtyMin,
    cenQtyMulti,
}: Props) => {
    const isMinError = isMinErrorObj?.isMinError;
    const isMultiError = isMultiErrorObj?.isMultiError;
    const { productInfo, product } = productContext;
    if (!product) {
        return null;
    }
    const [btnQty, setBtnQty] = React.useState(qtyOrdered);
    useEffect(() => {
        cenQtyMin({ isMinError: false });
        cenQtyMulti({ isMultiError: false });
    }, []);

    const [isFixedBottom, setIsFixedBottom] = React.useState(false);
    const qtyRef = React.useRef<HTMLDivElement | null>(null);
    const [showTooltip, setShowTooltip] = React.useState(false);
    const handleMouseEnter = () => {
        if (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only" && qtyOrdered === mutipleSaleQty) {
            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };
    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const isLaptopDesktop = () => window.innerWidth >= 768;
        const headerElement = document.querySelector("header") as HTMLHeadElement | null;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (isLaptopDesktop()) {
                    setIsFixedBottom(!entry.isIntersecting);
                    if (headerElement) {
                        headerElement.style.display = !entry.isIntersecting ? "none" : "block";
                    }
                }
            },
            { threshold: 0 },
        );

        // Observe the correct section based on current width
        const observeCorrectSection = () => {
            const newTarget =
                window.innerWidth <= 767
                    ? (document.querySelector(".mobileTabletSection") as HTMLDivElement)
                    : (document.querySelector(".desktopSection") as HTMLDivElement);

            if (newTarget && qtyRef.current !== newTarget) {
                if (qtyRef.current) {
                    observer.unobserve(qtyRef.current);
                }
                qtyRef.current = newTarget;
                observer.observe(newTarget);
            }
        };

        observeCorrectSection(); // initial observation

        window.addEventListener("resize", observeCorrectSection); // re-evaluate on resize

        return () => {
            window.removeEventListener("resize", observeCorrectSection);
            if (qtyRef.current) {
                observer.unobserve(qtyRef.current);
            }
            setIsFixedBottom(false);
            if (headerElement) {
                headerElement.style.display = "";
            }
        };
    }, []);

    const addQtyFn = () => {
        setBtnQty(
            qtyOrdered +
                (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only" ? mutipleSaleQty : 1),
        );
    };

    const subQtyFn = () => {
        const qtyValue =
            qtyOrdered -
            (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only" ? mutipleSaleQty : 1);
        if (qtyValue === 0 || Math.sign(qtyValue) === -1) {
            setBtnQty(0);
            return false;
        }
        setBtnQty(qtyValue);
    };
    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);
    return (
        <StyledWrapper
            {...styles.mainContainer}
            className={isFixedBottom ? "" : "hideBar"}
            data-test-selector="productDetails_scrollingbar"
        >
            <StyledWrapper {...styles.leftPanelRow}>
                <StyledWrapper {...styles.productImageWrapper}>
                    <ProductPrimaryImage product={product} image={image} extendedStyles={styles.primaryImage} />
                </StyledWrapper>

                <StyledWrapper {...styles.productTitleWrapper}>
                    {product.brand && (
                        <StyledWrapper {...styles.brandGridItem}>
                            <ProductBrand brand={product.brand} showLogo={false} extendedStyles={styles.brandStyles} />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.titleGridItem}>
                        <Typography
                            {...styles.titleText}
                            data-test-selector="productDetails_scrollingheader_productDescription"
                        >
                            {product.productTitle}
                        </Typography>

                        {product?.manufacturerItem && (
                            <Typography {...styles.valueText} as="p">
                                <Typography {...styles.labelText}>{translate("MPN:")}</Typography>
                                <Typography {...styles.labelValue}> {product.manufacturerItem}</Typography>
                            </Typography>
                        )}

                        {product?.productNumber && (
                            <Typography {...styles.valueText} as="p" data-test-selector="ProductDetailsPartNumber">
                                <Typography {...styles.labelText}>{translate("Part Number:")}</Typography>
                                <Typography {...styles.labelValue}> {product.productNumber}</Typography>
                            </Typography>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.rightPanelRow}>
                <StyledWrapper {...styles.productPriceWrapper}>
                    {productContext?.product?.isVariantParent ? (
                        <>
                            <Typography
                                as="p"
                                css={css`
                                    margin: 18px 0px 18px 6px;
                                    color: ${COLORS.text.mineShaft};
                                    font-family: ${NISCO.TYPOGRAPHY.fontFamily.title};
                                    font-size: ${TYPOGRAPHY.size.large};
                                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                                    line-height: ${TYPOGRAPHY.lineHeight.base};
                                    text-align: end;
                                `}
                            >
                                {siteMessage("Parent_Variant_PricingMessage")}
                            </Typography>
                        </>
                    ) : (
                        <NISProductPriceCustomPDP
                            product={productContext}
                            showLabel={false}
                            showSavings={true}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.productPrice}
                        />
                    )}
                </StyledWrapper>

                <StyledWrapper {...styles.productQTyWrapper}>
                    <StyledWrapper {...styles.quantityOrderedWrapper}>
                        <Button
                            disabled={
                                !!(
                                    (!variantSelectionCompleted && isVariantParent) ||
                                    (mutipleSaleQty &&
                                        mutipleSaleQty > 0 &&
                                        roundingRule === "Multiple Only" &&
                                        qtyOrdered === mutipleSaleQty)
                                )
                            }
                            {...styles.subQTYBtn}
                            data-test-selector="subQTYBtn"
                            onClick={subQtyFn}
                            onMouseOver={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <ButtonIcon src={Minus} />
                        </Button>
                        {showTooltip && (
                            <StyledWrapper {...styles.tooltipContainerStyles}>
                                <Typography
                                    as="span"
                                    css={css`
                                        color: ${getColor("info.contrast")} !important;
                                    `}
                                >
                                    {translate(`${mutipleSaleQty} unit minimum`)}
                                </Typography>
                            </StyledWrapper>
                        )}
                        <Typography {...styles.labelQtyText} as="span">
                            {translate("QTY:")}
                        </Typography>
                        {
                            <ProductQuantityOrdered
                                configurationSelection={configurationSelection}
                                configurationCompleted={configurationCompleted}
                                variantSelectionCompleted={variantSelectionCompleted}
                                extendedStyles={styles.quantityOrdered}
                                onBtnclickQtyValue={btnQty}
                                setBtnQty={setBtnQty}
                            />
                        }
                        <Button
                            disabled={!variantSelectionCompleted && isVariantParent}
                            {...styles.addQTYBtn}
                            data-test-selector="addQTYBtn"
                            onClick={addQtyFn}
                        >
                            <ButtonIcon src={Plus} />
                        </Button>
                    </StyledWrapper>
                    {isMultiError === true && (
                        <StyledWrapper {...styles.errorMsgStyle}>
                            <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                            <Typography {...styles.errorMsgStyleTypo}>
                                {translate(`This product must be ordered in multiples of ${mutipleSaleQty}`)}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {isMinError === true && (
                        <StyledWrapper {...styles.errorMsgStyle}>
                            <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                            <Typography {...styles.errorMsgStyleTypo}>
                                {translate(`This product has a minimum order of ${mutipleSaleQty} units`)}
                            </Typography>
                        </StyledWrapper>
                    )}
                </StyledWrapper>

                <StyledWrapper {...styles.productAddtoCartWrapper}>
                    <ProductAddToCartButton
                        configurationSelection={configurationSelection}
                        configurationCompleted={configurationCompleted}
                        variantSelectionCompleted={variantSelectionCompleted}
                        subscription={subscription}
                        extendedStyles={styles.button}
                        data-test-selector="addProductToCart"
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(NISPdpScrollingHeader)),
    definition: {
        displayName: "Nisco Product Detail Scrolling bar",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
